import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Filter, AlertCircle, Search, Calendar, ChevronLeft, ChevronRight, X, Image, Reply } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { apiCall, default as API_CONFIG } from '../config/api';
import { chipStyle } from './reportHelpers';

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [dateRange, setDateRange] = useState('all'); // 'all', 'today', 'week', 'month', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For controlled input
  // What the backend actually matched on. A symbol search gets widened across
  // every name the security is known by (LNXSY -> LXS, LXS.DE, Lanxess), and
  // since those clauses are substring matches a result can look unrelated to
  // what was typed. Showing the expansion is what makes such a hit explicable.
  const [searchExpansion, setSearchExpansion] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [onlyRelevant, setOnlyRelevant] = useState(true); // Hide personal / non-investment messages by default

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate date range
      let startDate = null;
      let endDate = null;
      const now = new Date();

      switch (dateRange) {
        case 'today':
          startDate = startOfDay(now).toISOString();
          endDate = endOfDay(now).toISOString();
          break;
        case 'week':
          startDate = startOfDay(subDays(now, 7)).toISOString();
          endDate = endOfDay(now).toISOString();
          break;
        case 'month':
          startDate = startOfDay(subDays(now, 30)).toISOString();
          endDate = endOfDay(now).toISOString();
          break;
        case 'custom':
          if (customStartDate) startDate = startOfDay(new Date(customStartDate)).toISOString();
          if (customEndDate) endDate = endOfDay(new Date(customEndDate)).toISOString();
          break;
        default:
          // 'all' - no date filtering
          break;
      }

      const endpoint = API_CONFIG.endpoints.messagesWithParams({
        page: currentPage,
        pageSize,
        startDate,
        endDate,
        search: searchText || undefined,
        onlyRelevant,
      });

      const data = await apiCall(endpoint);

      setMessages(data.messages || []);
      setSearchExpansion(data.search || null);
      setTotalCount(data.pagination?.totalCount || 0);
      setTotalPages(data.pagination?.totalPages || 0);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError('Failed to load messages. Please try again.');
      setLoading(false);
    }
  }, [currentPage, pageSize, dateRange, customStartDate, customEndDate, searchText, onlyRelevant]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, customStartDate, customEndDate, searchText, pageSize, onlyRelevant]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchText(searchInput);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchText('');
  };

  const clearAllFilters = () => {
    setDateRange('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setSearchInput('');
    setSearchText('');
  };

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const formatMessageDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);

    if (diffHours < 24) {
      return format(date, 'h:mm a');
    } else if (diffHours < 168) { // 7 days
      return format(date, 'EEE, h:mm a');
    } else {
      return format(date, 'MMM d, yyyy');
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="messages-pagination">
        <div className="pagination-info">
          Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount} messages
        </div>
        <div className="pagination-controls">
          <button
            className="btn btn-secondary pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>

          {startPage > 1 && (
            <>
              <button
                className="btn btn-secondary pagination-btn"
                onClick={() => setCurrentPage(1)}
              >
                1
              </button>
              {startPage > 2 && <span className="pagination-ellipsis">...</span>}
            </>
          )}

          {pages.map(page => (
            <button
              key={page}
              className={`btn pagination-btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
              <button
                className="btn btn-secondary pagination-btn"
                onClick={() => setCurrentPage(totalPages)}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            className="btn btn-secondary pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="pagination-page-size">
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="page-size-select"
          >
            <option value={10}>10 per page</option>
            <option value={15}>15 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="error">
        <AlertCircle size={24} />
        <p>{error}</p>
        <button className="btn btn-primary" onClick={fetchMessages}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Premium Russ Alerts</h1>
          <p>Recent messages from Russ's Telegram group</p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="card messages-filters-card">
        <button
          className="filters-toggle"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="filters-toggle-left">
            <Filter size={18} />
            <span>Filters</span>
            {(dateRange !== 'all' || searchText) && (
              <span className="badge badge-info">Active</span>
            )}
          </div>
          <span className={`filters-toggle-icon ${showFilters ? 'open' : ''}`}>
            <ChevronRight size={18} />
          </span>
        </button>

        {showFilters && (
          <div className="filters-content">
            {/* Search */}
            <div className="filter-group">
              <label className="filter-label">
                <Search size={14} />
                Search Messages
              </label>
              <form onSubmit={handleSearch} className="search-form">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by content or summary..."
                    className="search-input"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      className="search-clear"
                      onClick={clearSearch}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <button type="submit" className="btn btn-primary">
                  Search
                </button>
              </form>
            </div>

            {/* Date Range */}
            <div className="filter-group">
              <label className="filter-label">
                <Calendar size={14} />
                Date Range
              </label>
              <div className="date-range-buttons">
                {dateRangeOptions.map(option => (
                  <button
                    key={option.value}
                    className={`btn ${dateRange === option.value ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setDateRange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {dateRange === 'custom' && (
                <div className="custom-date-range">
                  <div className="date-input-group">
                    <label>From:</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="date-input"
                    />
                  </div>
                  <div className="date-input-group">
                    <label>To:</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="date-input"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Relevance toggle */}
            <div className="filter-group">
              <label className="filter-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={!onlyRelevant}
                  onChange={(e) => setOnlyRelevant(!e.target.checked)}
                  style={{ margin: 0 }}
                />
                <span>
                  Show non-investment content
                  <span style={{ marginLeft: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.8125rem', fontWeight: 400 }}>
                    (personal / off-topic messages classified by analyzer)
                  </span>
                </span>
              </label>
            </div>

            {/* Clear Filters */}
            {(dateRange !== 'all' || searchText || !onlyRelevant) && (
              <button
                className="btn btn-secondary clear-filters-btn"
                onClick={() => { clearAllFilters(); setOnlyRelevant(true); }}
              >
                <X size={14} />
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="messages-results-summary">
        <span>{totalCount} message{totalCount !== 1 ? 's' : ''} found</span>
        {searchText && <span className="search-badge">Search: "{searchText}"</span>}
        {searchExpansion?.expanded && (
          <span
            className="search-badge"
            title={
              `${searchExpansion.displayName || searchExpansion.canonicalSymbol} also trades as `
              + `${searchExpansion.terms.join(', ')}. Searching all of them.`
            }
          >
            + also matching {searchExpansion.terms
              .filter((t) => t.toUpperCase() !== searchText.toUpperCase())
              .join(', ')}
          </span>
        )}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          {/* Messages List */}
          <div className="messages-list">
            {messages.map((message) => {
              const tickers = message.extractedTickers || [];
              const themes = message.extractedThemes || [];
              const isPersonal = message.isRelevant === false;
              return (
                <div
                  key={message.id}
                  className="card message-card"
                  style={isPersonal ? { opacity: 0.7 } : undefined}
                >
                  <div className="message-card-inner">
                    <div className="message-avatar">
                      <MessageSquare size={18} style={{ color: 'white' }} />
                    </div>

                    <div className="message-content-wrapper">
                      <div className="message-header">
                        <div className="message-badges">
                          <span className="message-author">
                            {message.senderName || 'Russ'}
                          </span>
                          {message.isFromRuss && (
                            <span className="badge badge-success">Russ</span>
                          )}
                          {isPersonal && (
                            <span className="badge badge-info" title="Classified as non-investment by the analyzer">
                              personal
                            </span>
                          )}
                          {message.hasMedia && (
                            <span
                              className="badge badge-info"
                              title={message.mediaType ? `Media: ${message.mediaType}` : 'Has media attachment'}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                            >
                              <Image size={11} />
                              {message.mediaType || 'media'}
                            </span>
                          )}
                          {message.forwardedFrom && (
                            <span className="badge badge-info" title={`Forwarded from ${message.forwardedFrom}`}>
                              fwd
                            </span>
                          )}
                          {message.replyToMessageId && (
                            <span
                              className="badge badge-info"
                              title="Reply to another message"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                            >
                              <Reply size={11} />
                              reply
                            </span>
                          )}
                        </div>
                        <span className="message-time">
                          {formatMessageDate(message.date)}
                        </span>
                      </div>

                      <p className="message-text">
                        {message.content}
                      </p>

                      {(tickers.length > 0 || themes.length > 0) && (
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.375rem',
                          marginTop: '0.5rem',
                        }}>
                          {tickers.map((t) => (
                            <span key={`tk-${t}`} style={chipStyle('blue')}>{t}</span>
                          ))}
                          {themes.map((th) => (
                            <span key={`th-${th}`} style={chipStyle('purple')}>{th}</span>
                          ))}
                        </div>
                      )}

                      {message.summary && (
                        <div className="message-summary">
                          <strong>Summary:</strong> {message.summary}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {messages.length === 0 && !loading && (
              <div className="empty-state">
                <MessageSquare size={48} />
                <p>No messages found</p>
                {/* The exact dead end that motivated the alias layer: a
                    holding's own ticker finds nothing because Russ writes the
                    native listing. Say so, rather than implying he never
                    mentioned it. */}
                {searchExpansion?.symbolShaped && !searchExpansion?.expanded && (
                  <p style={{ fontSize: '0.875rem', maxWidth: '32rem' }}>
                    Nothing contains &ldquo;{searchExpansion.query}&rdquo;. If Russ refers to
                    this company by a different ticker &mdash; a native-market listing, or the
                    company name &mdash; link them on the{' '}
                    <Link to="/securities">Securities</Link> page and this search will find
                    both.
                  </p>
                )}
                {(dateRange !== 'all' || searchText) && (
                  <button
                    className="btn btn-primary"
                    onClick={clearAllFilters}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default Messages;
